import React, { useState } from 'react';
import { UserPlus, Check, X, MessageCircle, Loader } from 'lucide-react';

// Connect Button Component
export const ConnectButton = ({ alumniId, onSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConnect = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/connections/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          receiverId: alumniId,
          inviteMessage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send connection request');
      }

      setIsModalOpen(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <UserPlus className="h-4 w-4 mr-2" />
        Connect
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Send Connection Request
            </h3>
            
            <textarea
              value={inviteMessage}
              onChange={(e) => setInviteMessage(e.target.value)}
              placeholder="Add a note to your invitation..."
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="4"
            />

            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}

            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConnect}
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  'Send Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Connection Request Card
export const ConnectionRequestCard = ({ connection, onAccept, onReject }) => {
  const [loading, setLoading] = useState(false);

  const handleResponse = async (status) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/connections/${connection._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) throw new Error('Failed to update connection');

      if (status === 'accepted' && onAccept) onAccept();
      if (status === 'rejected' && onReject) onReject();
    } catch (err) {
      console.error('Error updating connection:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <img
          src={connection.sender.personalInfo?.avatar || '/default-avatar.png'}
          alt=""
          className="h-12 w-12 rounded-full"
        />
        <div>
          <h4 className="text-sm font-medium text-gray-900">
            {connection.sender.firstName} {connection.sender.lastName}
          </h4>
          {connection.inviteMessage && (
            <p className="text-sm text-gray-500 mt-1">
              {connection.inviteMessage}
            </p>
          )}
        </div>
      </div>
      
      <div className="flex space-x-2">
        <button
          onClick={() => handleResponse('accepted')}
          disabled={loading}
          className="p-2 text-green-600 hover:bg-green-50 rounded-full"
        >
          <Check className="h-5 w-5" />
        </button>
        <button
          onClick={() => handleResponse('rejected')}
          disabled={loading}
          className="p-2 text-red-600 hover:bg-red-50 rounded-full"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default { ConnectButton, ConnectionRequestCard };